/**
 * Choisir la machine d'un nouveau bon d'intervention : on tape une partie du nom, du modèle, du numéro de série ou
 * du client, et on choisit dans la liste. Un bon est toujours sur UNE machine (mono-machine : défaut assumé du CDC B02),
 * SAUF une livraison, qui peut se faire sur un site seul (on livre une pièce, un kit, un compresseur neuf pas encore au
 * parc) : un second onglet, « Livraison sans machine », cherche alors un site à la place.
 *
 * Panneau latéral comme les autres saisies : la liste des bons reste visible derrière.
 */
import { useEffect, useState } from 'react';

import { ApiError, api, type Asset, type Site } from '../api/client';
import { Panneau } from './Formulaire';

/** La recherche part après cette pause de frappe : une requête par touche serait du bruit. */
const PAUSE_DE_FRAPPE_MS = 250;
const CARACTERES_MINIMUM = 2;
/** Combien de sites on propose d'un coup : la recherche se précise en tapant. */
const SITES_AFFICHES = 10;

type Mode = 'machine' | 'site';

export function ChoixMachineBon({
  onChoisie,
  onChoisieSite,
  onFermer,
}: {
  onChoisie: (machine: { readonly id: string; readonly libelle: string }) => void;
  /** Une livraison sans machine : le site sur lequel le bon sera saisi. */
  onChoisieSite: (site: { readonly id: string; readonly libelle: string }) => void;
  onFermer: () => void;
}) {
  const [mode, setMode] = useState<Mode>('machine');
  const [saisie, setSaisie] = useState('');
  const [machines, setMachines] = useState<readonly Asset[] | null>(null);
  const [sites, setSites] = useState<readonly Site[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    const texte = saisie.trim();
    if (texte.length < CARACTERES_MINIMUM) {
      setMachines(null);
      setSites(null);
      return;
    }
    let annule = false;
    const minuteur = window.setTimeout(() => {
      const lecture =
        mode === 'machine'
          ? api.assets({ search: texte, take: 10 }).then((r) => {
              if (!annule) setMachines(r.items);
            })
          : api.sites(texte).then((r) => {
              if (!annule) setSites(r.slice(0, SITES_AFFICHES));
            });
      lecture
        .then(() => {
          if (!annule) setErreur(null);
        })
        .catch((e: unknown) => {
          if (!annule) setErreur(e instanceof ApiError ? e.message : 'Recherche impossible.');
        });
    }, PAUSE_DE_FRAPPE_MS);
    return () => {
      annule = true;
      window.clearTimeout(minuteur);
    };
  }, [saisie, mode]);

  const changerMode = (suivant: Mode) => {
    setMode(suivant);
    setSaisie('');
    setMachines(null);
    setSites(null);
    setErreur(null);
  };

  return (
    <Panneau
      titre="Nouveau bon d’intervention"
      sousTitre={
        mode === 'machine'
          ? 'Sur quelle machine l’intervention a-t-elle eu lieu ?'
          : 'Sur quel site la livraison a-t-elle eu lieu ? (sans machine)'
      }
      onFermer={onFermer}
      actions={
        <button type="button" className="secondaire" onClick={onFermer}>
          Annuler
        </button>
      }
    >
      <div className="chips-secondaires" role="group" aria-label="Sur quoi porte le bon">
        <button
          type="button"
          className={`chip${mode === 'machine' ? ' actif' : ''}`}
          aria-pressed={mode === 'machine'}
          onClick={() => changerMode('machine')}
        >
          Sur une machine
        </button>
        <button
          type="button"
          className={`chip${mode === 'site' ? ' actif' : ''}`}
          aria-pressed={mode === 'site'}
          onClick={() => changerMode('site')}
          title="Une livraison peut se faire sur un site seul, sans machine au parc"
        >
          Livraison sans machine
        </button>
      </div>

      <div className="champ">
        <label htmlFor="choix-machine">{mode === 'machine' ? 'Machine' : 'Site'}</label>
        <input
          id="choix-machine"
          type="text"
          autoFocus
          value={saisie}
          placeholder={mode === 'machine' ? 'Nom, modèle, n° de série, client…' : 'Nom du site, ville, client…'}
          onChange={(e) => setSaisie(e.target.value)}
        />
        <p className="aide">
          Tapez au moins {CARACTERES_MINIMUM} caractères.
          {mode === 'site' && ' Seule une livraison peut se saisir sans machine : les autres natures en exigent une.'}
        </p>
      </div>

      {erreur && <div className="message erreur">{erreur}</div>}

      {mode === 'machine' && machines !== null && machines.length === 0 && <div className="vide">Aucune machine ne correspond.</div>}
      {mode === 'machine' && machines !== null && machines.length > 0 && (
        <ul className="liste-choix-machine">
          {machines.map((m) => {
            const libelle = m.label ?? [m.model, m.serialNumber ? `n° ${m.serialNumber}` : null].filter(Boolean).join(' ') ?? 'Machine';
            return (
              <li key={m.id}>
                <button
                  type="button"
                  className="choix-machine"
                  onClick={() => onChoisie({ id: m.id, libelle: libelle || 'Machine sans désignation' })}
                >
                  <strong>{libelle || 'Machine sans désignation'}</strong>
                  <span className="aide">
                    {[m.customerName, m.siteName].filter(Boolean).join(' · ') || 'Sans client ni site'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {mode === 'site' && sites !== null && sites.length === 0 && <div className="vide">Aucun site ne correspond.</div>}
      {mode === 'site' && sites !== null && sites.length > 0 && (
        <ul className="liste-choix-machine">
          {sites.map((s) => (
            <li key={s.id}>
              <button type="button" className="choix-machine" onClick={() => onChoisieSite({ id: s.id, libelle: s.name })}>
                <strong>{s.name}</strong>
                <span className="aide">{[s.customerName, s.city].filter(Boolean).join(' · ') || 'Sans client ni ville'}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panneau>
  );
}
